import React, { useEffect, useState } from "react";
import api, { formatDate, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, Empty } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, HeartPulse, BarChart3, Lock, Trash2 } from "lucide-react";

const HR_SET = new Set(["super_admin", "hr_admin", "hr_staff", "general_manager", "accounting_manager"]);

// Mirrors the backend's standard pulse questions — shown pre-filled and editable.
const DEFAULT_PULSE_QS = [
    "I have what I need to do my job well.",
    "My workload is sustainable.",
    "I feel recognized for my work.",
    "I see myself here in 12 months.",
];
const KIND_LABEL = { scale: "Scale 1–5", enps: "Score 0–10", text: "Text answer" };
const blankForm = () => ({
    title: "", description: "", survey_type: "pulse", anonymous: true,
    questions: DEFAULT_PULSE_QS.map((t) => ({ text: t, kind: "scale" })),
});

export default function Engagement() {
    const { user } = useAuth();
    const canManage = (user?.roles || []).some((r) => HR_SET.has(r.role));
    const [mine, setMine] = useState([]);
    const [all, setAll] = useState([]);
    const [answering, setAnswering] = useState(null); // survey
    const [answers, setAnswers] = useState({});
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState(blankForm());
    const [resultsFor, setResultsFor] = useState(null);
    const [results, setResults] = useState(null);

    const load = () => {
        api.get("/engagement/me/surveys").then((r) => setMine(r.data.items || [])).catch(() => setMine([]));
        if (canManage) api.get("/engagement/surveys").then((r) => setAll(r.data.items || [])).catch(() => {});
    };
    useEffect(() => { load(); }, [canManage]);

    const submit = async () => {
        try {
            await api.post(`/engagement/me/surveys/${answering.id}/respond`, { answers });
            toast.success(answering.anonymous ? "Submitted anonymously — thank you!" : "Submitted — thank you!");
            setAnswering(null); setAnswers({}); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const setType = (survey_type) => setForm((f) => ({
        ...f, survey_type,
        questions: survey_type === "pulse" ? DEFAULT_PULSE_QS.map((t) => ({ text: t, kind: "scale" }))
                 : survey_type === "custom" ? (f.questions.length ? f.questions : [{ text: "", kind: "scale" }])
                 : [],
    }));
    const setQ = (i, patch) => setForm((f) => ({ ...f, questions: f.questions.map((q, j) => j === i ? { ...q, ...patch } : q) }));
    const addQ = () => setForm((f) => ({ ...f, questions: [...f.questions, { text: "", kind: "scale" }] }));
    const delQ = (i) => setForm((f) => ({ ...f, questions: f.questions.filter((_, j) => j !== i) }));

    const create = async () => {
        if (!form.title.trim()) return toast.error("Title required");
        const questions = form.questions.filter((q) => q.text.trim());
        if (form.survey_type !== "enps" && !questions.length) return toast.error("Add at least one question");
        try {
            await api.post("/engagement/surveys", {
                title: form.title, description: form.description, survey_type: form.survey_type,
                anonymous: form.anonymous,
                ...(form.survey_type !== "enps" ? { questions } : {}),
            });
            toast.success("Survey launched"); setCreating(false); setForm(blankForm()); load();
        }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const openResults = async (s) => {
        setResultsFor(s); setResults(null);
        const { data } = await api.get(`/engagement/surveys/${s.id}/results`);
        setResults(data);
    };

    const closeSurvey = async (s) => {
        if (!window.confirm(`Close “${s.title}”? Employees will no longer be able to answer.`)) return;
        await api.post(`/engagement/surveys/${s.id}/close`); toast.success("Survey closed"); load();
    };

    return (
        <div>
            <PageHeader title="Engagement" subtitle="Pulse surveys and eNPS. Responses are anonymous by default — only aggregates are visible."
                actions={canManage ? (
                    <Dialog open={creating} onOpenChange={setCreating}>
                        <DialogTrigger asChild><button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="new-survey-btn"><Plus className="size-4" /> New Survey</button></DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                            <DialogHeader><DialogTitle>Launch a survey</DialogTitle></DialogHeader>
                            <div className="space-y-2">
                                <div><Label>Type</Label>
                                    <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form.survey_type} onChange={(e) => setType(e.target.value)} data-testid="survey-type">
                                        <option value="pulse">Pulse (standard questions — editable)</option>
                                        <option value="custom">Custom (write your own questions)</option>
                                        <option value="enps">eNPS (standard 0–10 + comment)</option>
                                    </select></div>
                                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. August Pulse Check" data-testid="survey-title" /></div>
                                <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

                                {form.survey_type === "enps" ? (
                                    <div className="rounded-md bg-muted/60 p-2 text-xs text-muted-foreground">
                                        eNPS uses the standard questions: <b>“How likely are you to recommend this company as a place to work?” (0–10)</b> plus a comment box.
                                    </div>
                                ) : (
                                    <div className="border-t pt-2">
                                        <div className="flex items-center justify-between">
                                            <Label>Questions</Label>
                                            <button className="text-xs text-primary flex items-center gap-1" onClick={addQ} data-testid="add-question"><Plus className="size-3" /> Add question</button>
                                        </div>
                                        {form.questions.map((q, i) => (
                                            <div key={i} className="flex items-center gap-2 mt-2">
                                                <Input value={q.text} onChange={(e) => setQ(i, { text: e.target.value })} placeholder={`Question ${i + 1}`} data-testid={`survey-q-${i}`} />
                                                <select className="h-9 rounded-md border border-border bg-background px-1.5 text-xs shrink-0" value={q.kind} onChange={(e) => setQ(i, { kind: e.target.value })} title="Answer type">
                                                    {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                                </select>
                                                {form.questions.length > 1 && <button className="text-destructive shrink-0" onClick={() => delQ(i)} title="Remove"><Trash2 className="size-4" /></button>}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <label className="flex items-start gap-2 text-sm border-t pt-3 cursor-pointer">
                                    <input type="checkbox" className="mt-0.5" checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} data-testid="survey-anon" />
                                    <span>
                                        <span className="font-medium inline-flex items-center gap-1"><Lock className="size-3" /> Anonymous replies</span>
                                        <span className="block text-xs text-muted-foreground">
                                            {form.anonymous
                                                ? "Identities are never stored — only aggregate results are visible."
                                                : "Off: responses can be linked to the employee. Respondents will see this."}
                                        </span>
                                    </span>
                                </label>
                            </div>
                            <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={create} data-testid="survey-launch">Launch</button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                ) : null} />

            {/* Answer section (everyone with an employee record) */}
            <h3 className="font-display font-semibold mb-2">Your open surveys</h3>
            {!mine.length && <Surface className="p-5"><Empty title="No open surveys" subtitle="You’re all caught up." /></Surface>}
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {mine.map((s) => (
                    <Surface key={s.id} className="p-4">
                        <div className="flex items-center justify-between"><span className="badge badge-info uppercase">{s.survey_type}</span>{s.anonymous && <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1"><Lock className="size-3" /> anonymous</span>}</div>
                        <div className="font-display font-semibold mt-2">{s.title}</div>
                        <div className="text-xs text-muted-foreground mt-1">{s.description || ""}</div>
                        {s.answered_by_me
                            ? <div className="mt-3 text-xs text-emerald-600">✓ Answered — thank you</div>
                            : <button onClick={() => { setAnswering(s); setAnswers({}); }} className="mt-3 h-9 w-full rounded-md bg-primary text-white text-sm font-medium" data-testid={`answer-${s.id}`}>Answer now</button>}
                    </Surface>
                ))}
            </div>

            {/* Manage & results (HR) */}
            {canManage && (
                <div className="mt-8">
                    <h3 className="font-display font-semibold mb-2">Manage &amp; results</h3>
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {all.map((s) => (
                            <Surface key={s.id} className="p-4">
                                <div className="flex items-center justify-between"><span className="badge badge-info uppercase">{s.survey_type}</span><span className={`badge ${s.status === "open" ? "badge-approved" : "badge-draft"}`}>{s.status}</span></div>
                                <div className="font-display font-semibold mt-2">{s.title}</div>
                                <div className="text-xs text-muted-foreground mt-1">{s.response_count} responses · {s.participation_pct}% participation</div>
                                <div className="flex items-center gap-3 mt-3 pt-3 border-t text-xs">
                                    <button className="text-primary flex items-center gap-1" onClick={() => openResults(s)} data-testid={`results-${s.id}`}><BarChart3 className="size-3" /> Results</button>
                                    {s.status === "open" && <button className="text-muted-foreground" onClick={() => closeSurvey(s)}>Close</button>}
                                </div>
                            </Surface>
                        ))}
                        {!all.length && <Surface className="p-5"><Empty title="No surveys yet" /></Surface>}
                    </div>
                </div>
            )}

            {/* Answer dialog */}
            <Dialog open={!!answering} onOpenChange={(v) => !v && setAnswering(null)}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{answering?.title}</DialogTitle></DialogHeader>
                    {answering && (
                        <div className="space-y-4">
                            {answering.anonymous
                                ? <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Lock className="size-3" /> Your answers are anonymous.</div>
                                : <div className="text-xs text-amber-600">Note: this survey is not anonymous — your name will be attached to your answers.</div>}
                            {answering.questions.map((q, i) => (
                                <div key={q.id}>
                                    <div className="text-sm font-medium">{i + 1}. {q.text}</div>
                                    {q.kind === "scale" && (
                                        <div className="flex items-center gap-2 mt-2">
                                            {[1, 2, 3, 4, 5].map((v) => (
                                                <button key={v} onClick={() => setAnswers((a) => ({ ...a, [q.id]: v }))}
                                                    className={`size-9 rounded-md border text-sm font-semibold ${answers[q.id] === v ? "bg-primary text-white border-primary" : ""}`}>{v}</button>
                                            ))}
                                            <span className="text-[10px] text-muted-foreground ml-1">1 = disagree · 5 = agree</span>
                                        </div>
                                    )}
                                    {q.kind === "enps" && (
                                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                            {Array.from({ length: 11 }, (_, v) => (
                                                <button key={v} onClick={() => setAnswers((a) => ({ ...a, [q.id]: v }))}
                                                    className={`size-8 rounded-md border text-xs font-semibold ${answers[q.id] === v ? "bg-primary text-white border-primary" : ""}`}>{v}</button>
                                            ))}
                                        </div>
                                    )}
                                    {q.kind === "text" && (
                                        <textarea className="mt-2 w-full h-20 rounded-md border border-border bg-background p-2 text-sm"
                                            value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submit} data-testid="submit-survey">Submit</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Results dialog */}
            <Dialog open={!!resultsFor} onOpenChange={(v) => !v && setResultsFor(null)}>
                <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Results — {resultsFor?.title}</DialogTitle></DialogHeader>
                    {results && (
                        <div className="space-y-4">
                            <div className="text-sm text-muted-foreground">{results.response_count} responses · {results.participation_pct}% participation</div>
                            {results.enps && (
                                <Surface className="p-4 text-center">
                                    <div className="text-xs uppercase tracking-wide text-muted-foreground">eNPS</div>
                                    <div className={`font-display text-4xl font-bold ${results.enps.score >= 30 ? "text-emerald-600" : results.enps.score >= 0 ? "text-amber-600" : "text-destructive"}`}>{results.enps.score}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{results.enps.promoters} promoters · {results.enps.passives} passives · {results.enps.detractors} detractors</div>
                                </Surface>
                            )}
                            {results.results.filter((q) => q.kind === "scale").map((q) => (
                                <div key={q.id}>
                                    <div className="text-sm font-medium">{q.text}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <div className="font-display text-lg font-bold w-12">{q.average ?? "—"}</div>
                                        <div className="flex-1 flex items-end gap-1 h-10">
                                            {[1, 2, 3, 4, 5].map((v) => {
                                                const c = q.distribution?.[String(v)] || 0;
                                                const max = Math.max(1, ...Object.values(q.distribution || {}));
                                                return <div key={v} title={`${v}: ${c}`} className="flex-1 bg-primary/70 rounded-t" style={{ height: `${(c / max) * 100}%`, minHeight: c ? 4 : 1 }} />;
                                            })}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {results.results.filter((q) => q.kind === "text" && q.comments?.length > 0).map((q) => (
                                <div key={q.id}>
                                    <div className="text-sm font-medium">{q.text}</div>
                                    <ul className="mt-1 space-y-1">{q.comments.map((c, i) => <li key={i} className="text-xs text-muted-foreground border-l-2 pl-2">“{c}”</li>)}</ul>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
